﻿import { useState, useEffect, useRef } from "react";
import { CrudFunctions } from "../../utilities/crud-functions";
import { ModalMetadata } from "../../models/modal-metadata";
import GlobalDialog from "../Modal/GlobalDialog";
import { NpiEntryData } from '../../models/NPI/npi-entry-data';
import { NpiEntryComponent } from '../../models/NPI/npi-entry-component';
import { NpiEntrySolvent } from '../../models/NPI/npi-entry-solvent';
import { ToastProps } from "../../models/toast-props";

import DataGrid, {
    Column,
    Selection,
    Button as ColumnButton,
    Summary,
    TotalItem,
    Toolbar,
    Item as ToolbarItem,
    Editing
} from 'devextreme-react/data-grid';


import { Button } from "devextreme-react/button";
import { Toast } from 'devextreme-react/toast';
import RadioGroup from 'devextreme-react/radio-group';
import { Autocomplete, type AutocompleteTypes } from 'devextreme-react/autocomplete';
import { TextArea } from "devextreme-react/text-area";
import { TextBox } from "devextreme-react/text-box";
import { NumberBox } from "devextreme-react/number-box";
import { SelectBox } from "devextreme-react/select-box";
import { CheckBox } from "devextreme-react/check-box";
import { Popup as DialogPopup } from 'devextreme-react/popup';
import { Validator, RequiredRule, EmailRule, StringLengthRule, ValidatorRef } from 'devextreme-react/validator';

const matrixTypes = [
    { id: 1, name: "Neat" },
    { id: 2, name: "Solution" }
]

const certificationTypes = [
    { id: 1, name: "CRM" },
    { id: 2, name: "RM" }
]

const materialNames = [
    "2-Phenoxyethanol (10024)",
    "ACETAMINOPHEN (10007)",
    "Acetonitrile (10032)",
    "AVOBENZONE (10004)",
    "BENZYL ALCOHOL (10009)",
    "Dextromethorphan Hydrobromide (10021)",
    "FRUCTOSE (10006)",
    "HOMOSALATE	(10005)",
    "METHANOL (10012)",
    "METHYL PARABEN (10003)",
    "OCTINOXATE	(10013)",
    "Propylparaben (10002)",
    "Salicylic Acid (10001)"
]

const containerTypes = [
    { id: 1, name: "bottle" },
    { id: 2, name: "ampoule" },
    { id: 3, name: "screwtop bottle" },
    { id: 4, name: "mason jar" },
    { id: 5, name: "IBC Tote" },
    { id: 6, name: "Railroad Tank Car" }
]

const VALIDATION_GROUP = "ValidationGroup";

export default function NpiEntryFormDemo() {
    const validatorRef = useRef<ValidatorRef>(null);

    const [npiEntryData, setNpiEntryData] =
        useState<NpiEntryData>();

    const [toastProps, setToastProps] =
        useState<ToastProps>();

    useEffect(() => {
        getAllData();
    }, []);

    return (
        <div className="pw-content">
            <div className="lq-title-bar">
                <div className="lq-main-title">NPI Entry Form Demo</div>
            </div>

            <div className="pw-comp-row">
                <div className="pw-form-unit pw-comp-20">
                    <div className="pw-form-unit-label">Matrix</div>
                    <RadioGroup id="matrixType" items={matrixTypes ? matrixTypes : []}
                        displayExpr='name' valueExpr='id' layout="horizontal"
                        value={npiEntryData && npiEntryData.matrixType ? npiEntryData.matrixType : null}
                        onValueChanged={onValueChanged} />
                </div>
            </div>

            {npiEntryData && npiEntryData.matrixType === 1 &&
                <div className="pw-form-unit pw-comp-30">
                    <div className="pw-form-unit-label">Material Name</div>
                    <Autocomplete id="materialName" className="selectbox"
                        dataSource={materialNames}
                        onValueChanged={onValueChanged}
                        value={npiEntryData && npiEntryData.materialName ? npiEntryData.materialName : null} >
                        <Validator validationGroup={VALIDATION_GROUP}>
                            <RequiredRule message="Material Name is required" />
                            <StringLengthRule
                                message="Maximum length is 150 characters"
                                max={150} />
                        </Validator>
                    </Autocomplete>
                </div>
            }

            <div className="pw-form-unit">
                <div className="pw-form-unit-label">Specification</div>
                <TextArea id="specification" className="selectbox"
                    onValueChanged={onValueChanged}
                    height="8rem"
                    value={npiEntryData && npiEntryData.specification ? npiEntryData.specification : null}>
                    <Validator validationGroup={VALIDATION_GROUP}>
                        <StringLengthRule
                            message="Maximum length is 300 characters"
                            max={300} />
                    </Validator>
                </TextArea>
            </div>

            <div className="pw-comp-row">
                <div className="pw-form-unit pw-comp-20">
                    <div className="pw-form-unit-label">Unit Volume</div>
                    <TextBox id="unitVolumeString" className="selectbox"
                        onValueChanged={onValueChanged}
                        value={npiEntryData && npiEntryData.unitVolumeString ? npiEntryData.unitVolumeString : null}>
                        <Validator validationGroup={VALIDATION_GROUP}>
                            <StringLengthRule
                                message="Maximum length is 50 characters"
                                max={50} />
                        </Validator>
                    </TextBox>
                </div>

                <div className="pw-form-unit pw-comp-20">
                    <div className="pw-form-unit-label">Pack Size</div>
                    <NumberBox id="packSize" className="selectbox"
                        onValueChanged={onValueChanged}
                        value={npiEntryData && npiEntryData.packSize ? npiEntryData.packSize : null} />
                </div>
            </div>

            <div className="pw-comp-row">
                <div className="pw-form-unit pw-comp-20">
                    <div className="pw-form-unit-label">Container Type</div>
                    <SelectBox id="containerType" className="selectbox"
                        items={containerTypes}
                        displayExpr="name" valueExpr="id"
                        onValueChanged={onValueChanged}
                        value={npiEntryData && npiEntryData.containerTypeId ? npiEntryData.containerTypeId : null} />
                </div>

                <div className="pw-form-unit pw-comp-20">
                    <div className="pw-form-unit-label">Estimated Demand (# Units)</div>
                    <NumberBox id="estimatedDemand" className="selectbox"
                        onValueChanged={onValueChanged}
                        value={npiEntryData && npiEntryData.estimatedDemand ? npiEntryData.estimatedDemand : null} />
                </div>
            </div>

            <div className="pw-form-unit">
                <div className="pw-form-unit-label">Special Handling Requirements</div>
                <TextArea id="specialHandlingRequirements" className="selectbox"
                    onValueChanged={onValueChanged}
                    height="8rem"
                    value={npiEntryData && npiEntryData.specialHandlingRequirements ? npiEntryData.specialHandlingRequirements : null}>
                    <Validator validationGroup={VALIDATION_GROUP}>
                        <StringLengthRule
                            message="Maximum length is 500 characters"
                            max={50} />
                    </Validator>
                </TextArea>
            </div>

            <div className="pw-comp-row">
                <div className="pw-form-unit">
                    <CheckBox id="certification" text="Certification"
                        value={npiEntryData && npiEntryData.certification ? npiEntryData.certification : false}
                        onValueChanged={onValueChanged} />
                </div>

                {npiEntryData && npiEntryData.certification &&
                    <div>
                        <div className="pw-form-unit pw-comp-20">
                            <RadioGroup id="certificationType" items={certificationTypes ? certificationTypes : []}
                                displayExpr='name' valueExpr='id' layout="horizontal"
                                value={npiEntryData && npiEntryData.certificationType ? npiEntryData.certificationType : null}
                                onValueChanged={onValueChanged} />
                        </div>
                    </div>
                }
            </div>

            <div className="pw-form-unit pw-margin-top-1">
                <div className="pw-form-unit-label">Special Labeling Requirements</div>
                <TextArea id="specialLabelingRequirements" className="selectbox"
                    onValueChanged={onValueChanged}
                    height="8rem"
                    value={npiEntryData && npiEntryData.specialLabelingRequirements ? npiEntryData.specialLabelingRequirements : null}>
                    <Validator validationGroup={VALIDATION_GROUP}>
                        <StringLengthRule
                            message="Maximum length is 300 characters"
                            max={500} />
                    </Validator>
                </TextArea>
            </div>

            {npiEntryData && npiEntryData.matrixType === 2 &&
                <div>
                    <DataGrid
                        height="16rem" width="100%"
                        dataSource={npiEntryData.components ? npiEntryData.components : []}
                        wordWrapEnabled={true}
                        allowColumnResizing={true}
                        columnResizingMode='widget'
                        keyExpr="methodId"
                        rowAlternationEnabled={true}
                        showRowLines={true}
                        onSelectionChanged={componentsSelectionChanged}
                        onRowUpdating={componentsRowUpdating}
                        showBorders={true}>

                        <Selection mode='single' />

                        <Column type="buttons" width="8rem">
                            <ColumnButton name="edit" hint="edit row" />
                            <ColumnButton name="delete" hint="delete row" />
                        </Column>

                        <Column dataField='name' caption="Component (Name, CAS, SMILES)" dataType='string' width='16rem' />
                        <Column dataField='concentration' dataType='number' width='7rem' />
                        <Column dataField='notes' dataType='string' />

                        <Toolbar>
                            <ToolbarItem location='before'>
                                <div className="pw-datagrid-toolbar-template">
                                    <div className="pw-datagrid-toobar-title">Components</div>
                                </div>
                            </ToolbarItem>
                            <ToolbarItem location='after' name="addRowButton" />
                        </Toolbar>

                        <Editing mode="row"
                            useIcons={true}
                            allowAdding={true}
                            allowUpdating={true}
                            allowDeleting={false} />
                    </DataGrid>
                </div>
            }

            {npiEntryData && npiEntryData.matrixType === 2 &&
                <div className="pw-margin-top-1">
                    <DataGrid
                        height="16rem" width="100%"
                        dataSource={npiEntryData.solvents ? npiEntryData.solvents : []}
                        wordWrapEnabled={true}
                        allowColumnResizing={true}
                        columnResizingMode='widget'
                        keyExpr="methodId"
                        rowAlternationEnabled={true}
                        showRowLines={true}
                        onSelectionChanged={solventsSelectionChanged}
                        onRowUpdating={solventsRowUpdating}
                        showBorders={true}>

                        <Selection mode='single' />

                        <Column type="buttons" width="8rem">
                            <ColumnButton name="edit" hint="edit row" />
                            <ColumnButton name="delete" hint="delete row" />
                        </Column>

                        <Column dataField='name' caption="Solvent" dataType='string' width='16rem' />
                        <Column dataField='weightOrVolume' dataType='number' width='7rem' />
                        <Column dataField='percent' dataType='number' width='7rem' />
                        <Column allowEditing={false} />

                        <Toolbar>
                            <ToolbarItem location='before'>
                                <div className="pw-datagrid-toolbar-template">
                                    <div className="pw-datagrid-toobar-title">Solvents</div>
                                </div>
                            </ToolbarItem>
                            <ToolbarItem location='after' name="addRowButton" />
                        </Toolbar>

                        <Summary>
                            <TotalItem valueFormat="###0.#"
                                column="percent"
                                displayFormat="Sum of Solvents: {0}"
                                summaryType="sum" />
                        </Summary>

                        <Editing mode="row"
                            useIcons={true}
                            allowAdding={true}
                            allowUpdating={true}
                            allowDeleting={false} />
                    </DataGrid>
                </div>
            }

            <div className="pw-comp-row">
                <div className="pw-form-unit pw-comp-20">
                    <div className="pw-form-unit-label">Applicant Name</div>
                    <TextBox id="applicantName" className="selectbox"
                        onValueChanged={onValueChanged}
                        value={npiEntryData && npiEntryData.applicantName ? npiEntryData.applicantName : null}>
                        <Validator validationGroup={VALIDATION_GROUP}>
                            <RequiredRule message="Applicant Name is required" />
                            <StringLengthRule
                                message="Maximum length is 60 characters"
                                max={60} />
                        </Validator>
                    </TextBox>
                </div>

                <div className="pw-form-unit pw-comp-20">
                    <div className="pw-form-unit-label">Email</div>
                    <TextBox id="emailAddress" className="selectbox"
                        onValueChanged={onValueChanged}
                        value={npiEntryData && npiEntryData.emailAddress ? npiEntryData.emailAddress : null}>
                        <Validator validationGroup={VALIDATION_GROUP}>
                            <RequiredRule message="Email is required" />
                            <EmailRule message="Email is invalid" />
                            <StringLengthRule
                                message="Maximum length is 60 characters"
                                max={120} />
                        </Validator>
                    </TextBox>
                </div>
            </div>

            <div className="pw-form-unit">
                <Button validationGroup={VALIDATION_GROUP} type='default'
                    text="Submit" onClick={onSubmitNpiEntryClick} />
            </div>

            <div style={{ height: "10rem" }} />

            {
                toastProps && toastProps.visible &&
                <Toast
                    visible={toastProps.visible}
                    message={toastProps.message}
                    type={toastProps.toastType}
                    onHiding={onToastHiding}
                    displayTime={600} />
            }

        </div>
    );

    function onValueChanged(e: any) {
        if (!e.event)
            return;

        console.log("onValueChanged");
        console.log(e);

       

        if (e.element && e.element.id) {
            const caller = e.element.id;

            setNpiEntryData({ ...npiEntryData, [caller]: e.value })
        }
    }

    function componentsSelectionChanged(e: any) {

    }

    function componentsRowUpdating(e: any) {

    }

    function solventsSelectionChanged(e: any) {

    }

    function solventsRowUpdating(e: any) {

    }


    const validateEditor = function (e) {
        const res = e.validationGroup.validate();
        res.status === "pending" && res.complete.then((r) => {
            console.log(r.status);
        });
    };


    function onSubmitNpiEntryClick(e:any) {
        //todo - check values
        // esp for email, applicant name

        console.log("onSubmitNpiEntryClick()");
        console.log(npiEntryData);

        if (e.validationGroup) {
            console.log("About to validate......");

            const res = e.validationGroup.validate();
            res.status === "pending" && res.complete.then((r) => {
                console.log("VAL: -----");
                console.log(r.status);
            });
        }



        /*
        const valResult = validatorRef.current?.instance().validate();
        console.log("valResult:");
        console.log(valResult);
        */

        submitNpiEntry();
    }

    async function submitNpiEntry() {
        await CrudFunctions.postData("npi/npientrydata", npiEntryData)
            .then(() => {
                showToast("NPI Application submitted", "success");
            })
    }

    function getAllData() {

        let newNpiData: NpiEntryData = {
            matrixType: 1,
            containerTypeId: 1,
            certificationType: 1,
            components: [],
            solvents: []
        }

        setNpiEntryData(newNpiData);
    }

    function onToastHiding() {
        let toastParams: ToastProps = {
            message: undefined,
            toastType: undefined,
            visible: false
        };

        setToastProps(toastParams);
    }

    function showToast(message: string, toastType: string) {
        let toastParams: ToastProps = {
            message: message,
            toastType: toastType,
            visible: true
        };

        setToastProps(toastParams);
    }

}


